import React, { useRef, useState } from 'react';
import {
  Box,
  Divider,
  TextField,
  Typography,
  Chip,
  Paper,
  Button,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  alpha,
  useTheme,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  InfoTip,
} from './shared';

// ============================================
// FORMULÁRIO: ACORDO DE ALIMENTOS, GUARDA E VISITAS
// ============================================

export const AcordoAlimentosGuardaVisitasForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Acordo de Alimentos, Guarda e Visitas"
        customDescription="Acordo extrajudicial sobre alimentos, guarda e regime de visitas"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Alimentante */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Alimentante (quem paga) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo, CPF, profissão, endereço..."
            value={values.dados_alimentante || ''}
            onChange={(e) => handleFieldChange('dados_alimentante', e.target.value)}
          />
        </Box>

        {/* Dados do Alimentando */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Alimentando (quem recebe) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo, CPF, data de nascimento (se menor)..."
            value={values.dados_alimentando || ''}
            onChange={(e) => handleFieldChange('dados_alimentando', e.target.value)}
          />
        </Box>

        {/* Dados do Representante Legal (se menor) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Representante Legal (se alimentando for menor)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Nome completo, CPF, parentesco..."
            value={values.representante_legal || ''}
            onChange={(e) => handleFieldChange('representante_legal', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Valor dos Alimentos */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Valor dos Alimentos <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Ex: R$ 1.500,00 mensais ou 30% do salário mínimo, ou 20% dos rendimentos líquidos..."
            value={values.valor_alimentos || ''}
            onChange={(e) => handleFieldChange('valor_alimentos', e.target.value)}
          />
        </Box>

        {/* Forma de Pagamento */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Forma e Data de Pagamento
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Ex: Depósito bancário até o dia 10 de cada mês na conta..."
            value={values.forma_pagamento || ''}
            onChange={(e) => handleFieldChange('forma_pagamento', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Guarda */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Tipo de Guarda <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <RadioGroup
            value={values.tipo_guarda || 'compartilhada'}
            onChange={(e) => handleFieldChange('tipo_guarda', e.target.value)}
          >
            <FormControlLabel value="compartilhada" control={<Radio />} label="Guarda Compartilhada" />
            <FormControlLabel value="unilateral_mae" control={<Radio />} label="Guarda Unilateral - Mãe" />
            <FormControlLabel value="unilateral_pai" control={<Radio />} label="Guarda Unilateral - Pai" />
            <FormControlLabel value="alternada" control={<Radio />} label="Guarda Alternada" />
          </RadioGroup>
        </Box>

        {/* Regime de Visitas */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Regime de Visitas <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Descreva detalhadamente o regime de visitas: finais de semana alternados, feriados, férias escolares, datas comemorativas (Natal, Ano Novo, Dia das Mães/Pais, aniversários), horários de busca e entrega..."
            value={values.regime_visitas || ''}
            onChange={(e) => handleFieldChange('regime_visitas', e.target.value)}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Certidão de nascimento, comprovantes de renda, documentos das partes."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com detalhes adicionais sobre o acordo."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="Este acordo pode ser homologado judicialmente para ter força de título executivo." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: ACORDO DE DIVÓRCIO COMPLETO
// ============================================

export const AcordoDivorcioCompletoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [novoBem, setNovoBem] = useState('');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleAdicionarBem = () => {
    if (novoBem.trim()) {
      const bens = values.bens_partilha || [];
      handleFieldChange('bens_partilha', [...bens, novoBem.trim()]);
      setNovoBem('');
    }
  };

  const handleRemoverBem = (index: number) => {
    const bens = values.bens_partilha || [];
    handleFieldChange('bens_partilha', bens.filter((_: any, i: number) => i !== index));
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Acordo de Divórcio, Partilha, Alimentos, Guarda e Visitas"
        customDescription="Acordo completo de divórcio consensual com todas as cláusulas"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Cônjuge 1 */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Cônjuge 1 <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo, nacionalidade, profissão, RG, CPF, endereço..."
            value={values.conjuge_1 || ''}
            onChange={(e) => handleFieldChange('conjuge_1', e.target.value)}
          />
        </Box>

        {/* Dados do Cônjuge 2 */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Cônjuge 2 <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo, nacionalidade, profissão, RG, CPF, endereço..."
            value={values.conjuge_2 || ''}
            onChange={(e) => handleFieldChange('conjuge_2', e.target.value)}
          />
        </Box>

        {/* Dados do Casamento */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Casamento <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Data do casamento, regime de bens, cartório onde foi realizado..."
            value={values.dados_casamento || ''}
            onChange={(e) => handleFieldChange('dados_casamento', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Filhos */}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.tem_filhos || false}
                onChange={(e) => handleFieldChange('tem_filhos', e.target.checked)}
              />
            }
            label="O casal possui filhos menores ou incapazes"
          />
        </Box>

        {values.tem_filhos && (
          <>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Dados dos Filhos
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Nome, data de nascimento e idade de cada filho..."
                value={values.dados_filhos || ''}
                onChange={(e) => handleFieldChange('dados_filhos', e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Guarda e Visitas
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Tipo de guarda, regime de visitas, feriados, férias..."
                value={values.guarda_visitas || ''}
                onChange={(e) => handleFieldChange('guarda_visitas', e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Pensão Alimentícia dos Filhos
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Valor, forma e data de pagamento, reajuste..."
                value={values.pensao_filhos || ''}
                onChange={(e) => handleFieldChange('pensao_filhos', e.target.value)}
              />
            </Box>
          </>
        )}

        <Divider />

        {/* Alimentos entre Cônjuges */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Alimentos entre Cônjuges
          </Typography>
          <RadioGroup
            value={values.alimentos_conjuge || 'dispensados'}
            onChange={(e) => handleFieldChange('alimentos_conjuge', e.target.value)}
          >
            <FormControlLabel value="dispensados" control={<Radio />} label="Dispensados mutuamente" />
            <FormControlLabel value="conjuge_1_paga" control={<Radio />} label="Cônjuge 1 paga ao Cônjuge 2" />
            <FormControlLabel value="conjuge_2_paga" control={<Radio />} label="Cônjuge 2 paga ao Cônjuge 1" />
          </RadioGroup>
          
          {values.alimentos_conjuge && values.alimentos_conjuge !== 'dispensados' && (
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              multiline
              rows={2}
              placeholder="Valor, prazo e condições..."
              value={values.valor_alimentos_conjuge || ''}
              onChange={(e) => handleFieldChange('valor_alimentos_conjuge', e.target.value)}
            />
          )}
        </Box>

        <Divider />

        {/* Partilha de Bens */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Partilha de Bens
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Descreva o bem e para quem ficará (Ex: Apartamento na Rua X - Cônjuge 1)"
              value={novoBem}
              onChange={(e) => setNovoBem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdicionarBem()}
            />
            <Button variant="outlined" onClick={handleAdicionarBem} startIcon={<AddIcon />}>
              Adicionar
            </Button>
          </Box>

          {(values.bens_partilha || []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(values.bens_partilha || []).map((bem: string, index: number) => (
                <Chip
                  key={index}
                  label={bem}
                  onDelete={() => handleRemoverBem(index)}
                  sx={{ justifyContent: 'space-between' }}
                />
              ))}
            </Box>
          )}

          <TextField
            fullWidth
            sx={{ mt: 2 }}
            multiline
            rows={4}
            placeholder="Ou descreva livremente a partilha de bens..."
            value={values.descricao_partilha || ''}
            onChange={(e) => handleFieldChange('descricao_partilha', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Nome após Divórcio */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Nome após o Divórcio
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Indicar se algum cônjuge voltará a usar o nome de solteiro(a)..."
            value={values.nome_apos_divorcio || ''}
            onChange={(e) => handleFieldChange('nome_apos_divorcio', e.target.value)}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Certidão de casamento, documentos dos cônjuges, certidões de nascimento dos filhos, documentos dos bens."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com detalhes adicionais."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="Este acordo pode ser feito por escritura pública em cartório se não houver filhos menores ou incapazes." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: ACORDO EXTRAJUDICIAL TRABALHISTA
// ============================================

export const AcordoExtrajudicialTrabalhistaExtForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Acordo Extrajudicial Trabalhista"
        customDescription="Acordo para homologação na Justiça do Trabalho (Art. 855-B da CLT)"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Empregador */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Empregador <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Razão social, CNPJ, endereço, representante legal..."
            value={values.dados_empregador || ''}
            onChange={(e) => handleFieldChange('dados_empregador', e.target.value)}
          />
        </Box>

        {/* Dados do Empregado */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Empregado <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo, CPF, CTPS, endereço..."
            value={values.dados_empregado || ''}
            onChange={(e) => handleFieldChange('dados_empregado', e.target.value)}
          />
        </Box>

        {/* Dados do Contrato de Trabalho */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Contrato de Trabalho <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Data de admissão, data de demissão, função, salário, motivo da rescisão..."
            value={values.dados_contrato || ''}
            onChange={(e) => handleFieldChange('dados_contrato', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Verbas Rescisórias */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Verbas Rescisórias e Valor do Acordo <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Discrimine as verbas: saldo de salário, aviso prévio, 13º proporcional, férias + 1/3, FGTS, multa 40%, horas extras, adicional noturno, etc. E o valor total do acordo."
            value={values.verbas_acordo || ''}
            onChange={(e) => handleFieldChange('verbas_acordo', e.target.value)}
          />
        </Box>

        {/* Forma de Pagamento */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Forma de Pagamento <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="À vista ou parcelado? Datas de pagamento, conta para depósito..."
            value={values.forma_pagamento || ''}
            onChange={(e) => handleFieldChange('forma_pagamento', e.target.value)}
          />
        </Box>

        {/* Quitação */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Extensão da Quitação
          </Typography>
          <RadioGroup
            value={values.tipo_quitacao || 'parcial'}
            onChange={(e) => handleFieldChange('tipo_quitacao', e.target.value)}
          >
            <FormControlLabel 
              value="parcial" 
              control={<Radio />} 
              label="Quitação parcial (apenas das verbas discriminadas)" 
            />
            <FormControlLabel 
              value="total" 
              control={<Radio />} 
              label="Quitação total do contrato de trabalho" 
            />
          </RadioGroup>
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="CTPS, contracheques, TRCT, documentos das partes."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com detalhes adicionais."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="O acordo deve ser levado à Justiça do Trabalho para homologação, sendo necessária a presença de advogados distintos para cada parte." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: CONTRANOTIFICAÇÃO EXTRAJUDICIAL
// ============================================

export const ContranotificacaoExtrajudicialForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Contranotificação Extrajudicial"
        customDescription="Resposta formal a uma notificação extrajudicial recebida"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Notificante (quem responde) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Contranotificante (seu cliente) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo/Razão social, CPF/CNPJ, endereço..."
            value={values.dados_contranotificante || ''}
            onChange={(e) => handleFieldChange('dados_contranotificante', e.target.value)}
          />
        </Box>

        {/* Dados do Notificado */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Notificante Original (parte contrária) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo/Razão social, CPF/CNPJ, endereço..."
            value={values.dados_notificante_original || ''}
            onChange={(e) => handleFieldChange('dados_notificante_original', e.target.value)}
          />
        </Box>

        {/* Notificação Original */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Resumo da Notificação Original Recebida <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Resuma o conteúdo da notificação extrajudicial recebida..."
            value={values.resumo_notificacao_original || ''}
            onChange={(e) => handleFieldChange('resumo_notificacao_original', e.target.value)}
          />
        </Box>

        {/* Argumentos de Defesa */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Argumentos e Resposta <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Apresente os argumentos de defesa, refutando os pontos da notificação original. Se houver contraproposta ou exigências, inclua aqui."
            value={values.argumentos_resposta || ''}
            onChange={(e) => handleFieldChange('argumentos_resposta', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Notificação original recebida, documentos comprobatórios, provas."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com mais detalhes sobre a resposta."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A contranotificação é uma resposta formal que pode evitar litígios ou constituir prova pré-constituída." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: HABILITAÇÃO DE CRÉDITO ADMINISTRATIVA
// ============================================

export const HabilitacaoCreditoAdministrativaExtForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Habilitação de Crédito Administrativa"
        customDescription="Habilitação em liquidação extrajudicial de instituição financeira"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Credor */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Credor <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo/Razão social, CPF/CNPJ, endereço, contatos..."
            value={values.dados_credor || ''}
            onChange={(e) => handleFieldChange('dados_credor', e.target.value)}
          />
        </Box>

        {/* Dados da Instituição em Liquidação */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados da Instituição em Liquidação <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Nome da instituição financeira, CNPJ..."
            value={values.dados_instituicao || ''}
            onChange={(e) => handleFieldChange('dados_instituicao', e.target.value)}
          />
        </Box>

        {/* Valor e Origem do Crédito */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Valor e Origem do Crédito <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva a origem do crédito (conta poupança, CDB, depósito à vista, etc.), valor principal, correção monetária, juros..."
            value={values.valor_credito || ''}
            onChange={(e) => handleFieldChange('valor_credito', e.target.value)}
          />
        </Box>

        {/* Classificação do Crédito */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Classificação do Crédito
          </Typography>
          <RadioGroup
            value={values.classificacao_credito || 'quirografario'}
            onChange={(e) => handleFieldChange('classificacao_credito', e.target.value)}
          >
            <FormControlLabel value="privilegiado" control={<Radio />} label="Crédito com garantia especial/privilegiado" />
            <FormControlLabel value="quirografario" control={<Radio />} label="Crédito quirografário" />
            <FormControlLabel value="subordinado" control={<Radio />} label="Crédito subordinado" />
          </RadioGroup>
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Extratos bancários, contratos, comprovantes de aplicação, documentos pessoais."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A habilitação deve ser apresentada ao liquidante dentro do prazo estabelecido no edital de liquidação." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: HABILITAÇÃO DE CRÉDITO RETARDATÁRIA
// ============================================

export const HabilitacaoCreditoRetardatariaExtForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Habilitação de Crédito Retardatária"
        customDescription="Habilitação após o prazo regular em liquidação extrajudicial"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Credor */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Credor <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo/Razão social, CPF/CNPJ, endereço, contatos..."
            value={values.dados_credor || ''}
            onChange={(e) => handleFieldChange('dados_credor', e.target.value)}
          />
        </Box>

        {/* Dados da Instituição */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados da Instituição em Liquidação <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Nome da instituição financeira, CNPJ..."
            value={values.dados_instituicao || ''}
            onChange={(e) => handleFieldChange('dados_instituicao', e.target.value)}
          />
        </Box>

        {/* Justificativa do Atraso */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Justificativa do Atraso <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Explique o motivo pelo qual a habilitação não foi feita no prazo regular..."
            value={values.justificativa_atraso || ''}
            onChange={(e) => handleFieldChange('justificativa_atraso', e.target.value)}
          />
        </Box>

        {/* Valor e Origem do Crédito */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Valor e Origem do Crédito <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva a origem do crédito, valor principal, correção monetária, juros..."
            value={values.valor_credito || ''}
            onChange={(e) => handleFieldChange('valor_credito', e.target.value)}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Extratos, contratos, comprovantes, documentos pessoais, prova da justificativa do atraso."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="Na habilitação retardatária, o crédito só concorrerá aos rateios posteriores à habilitação." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: IMPUGNAÇÃO DE CRÉDITO EM LIQUIDAÇÃO
// ============================================

export const ImpugnacaoCreditoLiquidacaoExtForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Impugnação de Crédito em Liquidação Extrajudicial"
        customDescription="Contestação de crédito habilitado em liquidação de instituição financeira"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Impugnante */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Impugnante <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo/Razão social, CPF/CNPJ, qualificação como credor..."
            value={values.dados_impugnante || ''}
            onChange={(e) => handleFieldChange('dados_impugnante', e.target.value)}
          />
        </Box>

        {/* Dados do Impugnado */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Credor Impugnado <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Nome/Razão social do credor cujo crédito está sendo impugnado..."
            value={values.dados_impugnado || ''}
            onChange={(e) => handleFieldChange('dados_impugnado', e.target.value)}
          />
        </Box>

        {/* Crédito Impugnado */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Crédito Impugnado <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Descreva o crédito que está sendo impugnado: valor, classificação, origem..."
            value={values.credito_impugnado || ''}
            onChange={(e) => handleFieldChange('credito_impugnado', e.target.value)}
          />
        </Box>

        {/* Fundamentos da Impugnação */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Fundamentos da Impugnação <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Apresente os motivos da impugnação: inexistência do crédito, valor incorreto, classificação inadequada, prescrição, etc."
            value={values.fundamentos_impugnacao || ''}
            onChange={(e) => handleFieldChange('fundamentos_impugnacao', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Quadro geral de credores, documentos do crédito impugnado, provas."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A impugnação deve ser apresentada no prazo estabelecido após publicação do quadro de credores." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: NOTIFICAÇÃO EXTRAJUDICIAL
// ============================================

export const NotificacaoExtrajudicialExtForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Notificação Extrajudicial"
        customDescription="Comunicação formal para constituição em mora ou exigência de cumprimento"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Notificante */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Notificante (seu cliente) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo/Razão social, CPF/CNPJ, endereço..."
            value={values.dados_notificante || ''}
            onChange={(e) => handleFieldChange('dados_notificante', e.target.value)}
          />
        </Box>

        {/* Dados do Notificado */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Notificado (parte contrária) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Nome completo/Razão social, CPF/CNPJ, endereço para envio..."
            value={values.dados_notificado || ''}
            onChange={(e) => handleFieldChange('dados_notificado', e.target.value)}
          />
        </Box>

        {/* Objetivo da Notificação */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Objetivo da Notificação <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Descreva o objetivo: cobrança de dívida, exigência de cumprimento de obrigação, rescisão de contrato, retratação, etc. Inclua valores, prazos e consequências do não atendimento."
            value={values.objetivo_notificacao || ''}
            onChange={(e) => handleFieldChange('objetivo_notificacao', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
        </Box>

        {/* Prazo para Resposta */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Prazo para Atendimento
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: 10 (dez) dias úteis, 48 horas..."
            value={values.prazo_atendimento || ''}
            onChange={(e) => handleFieldChange('prazo_atendimento', e.target.value)}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Contratos, notas fiscais, comprovantes de pagamento, correspondências anteriores."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com mais detalhes sobre a situação."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A notificação constitui prova pré-constituída e pode ser enviada via cartório, AR ou e-mail com confirmação." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: PARECER JURÍDICO
// ============================================

export const ParecerJuridicoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Parecer Jurídico"
        customDescription="Opinião técnica fundamentada sobre questão jurídica"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Solicitante */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Solicitante do Parecer <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Nome/Razão social de quem solicitou o parecer..."
            value={values.solicitante || ''}
            onChange={(e) => handleFieldChange('solicitante', e.target.value)}
          />
        </Box>

        {/* Assunto/Tema */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Assunto/Tema do Parecer <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: Viabilidade de ação judicial, análise de contrato, consulta tributária..."
            value={values.assunto_parecer || ''}
            onChange={(e) => handleFieldChange('assunto_parecer', e.target.value)}
          />
        </Box>

        {/* Consulta/Quesitos */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Consulta/Quesitos a Responder <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Descreva a consulta ou liste os quesitos que devem ser respondidos no parecer..."
            value={values.consulta_quesitos || ''}
            onChange={(e) => handleFieldChange('consulta_quesitos', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
        </Box>

        {/* Fatos Relevantes */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Fatos Relevantes <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Descreva os fatos que fundamentam a consulta..."
            value={values.fatos_relevantes || ''}
            onChange={(e) => handleFieldChange('fatos_relevantes', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Documentos relacionados à consulta, contratos, processos, legislação específica."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com mais detalhes sobre a consulta."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="O parecer será estruturado com: Ementa, Consulta, Fatos, Fundamentação Jurídica e Conclusão." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: INVENTÁRIO EXTRAJUDICIAL
// ============================================

export const InventarioExtrajudicialForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [novoHerdeiro, setNovoHerdeiro] = useState('');
  const [novoBem, setNovoBem] = useState('');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleAdicionarHerdeiro = () => {
    if (novoHerdeiro.trim()) {
      const herdeiros = values.herdeiros || [];
      handleFieldChange('herdeiros', [...herdeiros, novoHerdeiro.trim()]);
      setNovoHerdeiro('');
    }
  };

  const handleRemoverHerdeiro = (index: number) => {
    const herdeiros = values.herdeiros || [];
    handleFieldChange('herdeiros', herdeiros.filter((_: any, i: number) => i !== index));
  };

  const handleAdicionarBem = () => {
    if (novoBem.trim()) {
      const bens = values.bens_inventario || [];
      handleFieldChange('bens_inventario', [...bens, novoBem.trim()]);
      setNovoBem('');
    }
  };

  const handleRemoverBem = (index: number) => {
    const bens = values.bens_inventario || [];
    handleFieldChange('bens_inventario', bens.filter((_: any, i: number) => i !== index));
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Petição de Inventário Extrajudicial"
        customDescription="Inventário realizado em cartório quando todos os herdeiros são maiores e capazes"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Falecido */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Falecido (De Cujus) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Nome completo, data de nascimento, data de óbito, estado civil, último domicílio, profissão..."
            value={values.dados_falecido || ''}
            onChange={(e) => handleFieldChange('dados_falecido', e.target.value)}
          />
        </Box>

        {/* Herdeiros */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Herdeiros <Typography component="span" color="error.main">*</Typography>
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Nome, grau de parentesco, CPF, estado civil..."
              value={novoHerdeiro}
              onChange={(e) => setNovoHerdeiro(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdicionarHerdeiro()}
            />
            <Button variant="outlined" onClick={handleAdicionarHerdeiro} startIcon={<AddIcon />}>
              Adicionar
            </Button>
          </Box>

          {(values.herdeiros || []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {(values.herdeiros || []).map((herdeiro: string, index: number) => (
                <Chip
                  key={index}
                  label={herdeiro}
                  onDelete={() => handleRemoverHerdeiro(index)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Cônjuge Sobrevivente */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Cônjuge/Companheiro(a) Sobrevivente (se houver)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Nome, CPF, regime de bens do casamento/união estável..."
            value={values.conjuge_sobrevivente || ''}
            onChange={(e) => handleFieldChange('conjuge_sobrevivente', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Bens */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Bens a Inventariar <Typography component="span" color="error.main">*</Typography>
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Descrição do bem, localização, matrícula (se imóvel), valor estimado..."
              value={novoBem}
              onChange={(e) => setNovoBem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdicionarBem()}
            />
            <Button variant="outlined" onClick={handleAdicionarBem} startIcon={<AddIcon />}>
              Adicionar
            </Button>
          </Box>

          {(values.bens_inventario || []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {(values.bens_inventario || []).map((bem: string, index: number) => (
                <Chip
                  key={index}
                  label={bem}
                  onDelete={() => handleRemoverBem(index)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Dívidas */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dívidas do Espólio (se houver)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Descreva as dívidas deixadas pelo falecido..."
            value={values.dividas_espolio || ''}
            onChange={(e) => handleFieldChange('dividas_espolio', e.target.value)}
          />
        </Box>

        {/* Partilha */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Forma de Partilha
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva como os bens serão partilhados entre os herdeiros..."
            value={values.forma_partilha || ''}
            onChange={(e) => handleFieldChange('forma_partilha', e.target.value)}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Certidão de óbito, documentos do falecido e herdeiros, certidões de imóveis, extratos bancários, documentos de veículos."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="O inventário extrajudicial é possível quando todos os herdeiros são maiores, capazes e concordam com a partilha." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: PROCURAÇÃO
// ============================================

export const ProcuracaoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const poderesComuns = [
    { id: 'foro_geral', label: 'Poderes da cláusula "ad judicia" (foro em geral)' },
    { id: 'acordo', label: 'Fazer acordos' },
    { id: 'receber', label: 'Receber e dar quitação' },
    { id: 'desistir', label: 'Desistir' },
    { id: 'renunciar', label: 'Renunciar' },
    { id: 'transigir', label: 'Transigir' },
    { id: 'substabelecer', label: 'Substabelecer com ou sem reserva de poderes' },
    { id: 'confessar', label: 'Confessar' },
    { id: 'representar_administrativo', label: 'Representar perante órgãos administrativos' },
    { id: 'assinar_documentos', label: 'Assinar documentos' },
  ];

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handlePoderToggle = (poderId: string) => {
    const poderesSelecionados = values.poderes_selecionados || [];
    const novosPoderes = poderesSelecionados.includes(poderId)
      ? poderesSelecionados.filter((p: string) => p !== poderId)
      : [...poderesSelecionados, poderId];
    handleFieldChange('poderes_selecionados', novosPoderes);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Procuração"
        customDescription="Instrumento de mandato para representação"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Tipo de Procuração */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Tipo de Procuração <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <RadioGroup
            value={values.tipo_procuracao || 'ad_judicia'}
            onChange={(e) => handleFieldChange('tipo_procuracao', e.target.value)}
          >
            <FormControlLabel value="ad_judicia" control={<Radio />} label="Ad Judicia (para representação em processos judiciais)" />
            <FormControlLabel value="ad_negotia" control={<Radio />} label="Ad Negotia (para negócios extrajudiciais)" />
            <FormControlLabel value="ad_judicia_et_extra" control={<Radio />} label="Ad Judicia et Extra (para fins judiciais e extrajudiciais)" />
          </RadioGroup>
        </Box>

        {/* Dados do Outorgante */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Outorgante (quem concede os poderes) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Nome completo, nacionalidade, estado civil, profissão, RG, CPF, endereço completo..."
            value={values.dados_outorgante || ''}
            onChange={(e) => handleFieldChange('dados_outorgante', e.target.value)}
          />
        </Box>

        {/* Dados do Outorgado */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Outorgado (advogado/procurador) <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Nome completo, nacionalidade, estado civil, profissão, OAB, CPF, endereço profissional..."
            value={values.dados_outorgado || ''}
            onChange={(e) => handleFieldChange('dados_outorgado', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Poderes */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Poderes Conferidos
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {poderesComuns.map((poder) => (
              <Paper
                key={poder.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: (values.poderes_selecionados || []).includes(poder.id)
                    ? 'primary.main'
                    : 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: (values.poderes_selecionados || []).includes(poder.id)
                    ? alpha(theme.palette.primary.main, 0.05)
                    : 'background.paper',
                }}
                onClick={() => handlePoderToggle(poder.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={(values.poderes_selecionados || []).includes(poder.id)}
                    sx={{ p: 0 }}
                  />
                  <Typography variant="body2">{poder.label}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Poderes Específicos */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Poderes Específicos Adicionais
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva poderes específicos não listados acima..."
            value={values.poderes_especificos || ''}
            onChange={(e) => handleFieldChange('poderes_especificos', e.target.value)}
          />
        </Box>

        {/* Finalidade */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Finalidade da Procuração
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Para que fim específico está sendo outorgada a procuração? (processo específico, atos administrativos, etc.)"
            value={values.finalidade_procuracao || ''}
            onChange={(e) => handleFieldChange('finalidade_procuracao', e.target.value)}
          />
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Documentos do outorgante (RG, CPF, comprovante de endereço)."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A procuração pode ser pública (em cartório) ou particular, dependendo do ato a ser praticado." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: TESTAMENTO PARTICULAR
// ============================================

export const TestamentoParticularForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [novoHerdeiro, setNovoHerdeiro] = useState('');
  const [novoLegado, setNovoLegado] = useState('');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleAdicionarHerdeiro = () => {
    if (novoHerdeiro.trim()) {
      const herdeiros = values.herdeiros_testamentarios || [];
      handleFieldChange('herdeiros_testamentarios', [...herdeiros, novoHerdeiro.trim()]);
      setNovoHerdeiro('');
    }
  };

  const handleRemoverHerdeiro = (index: number) => {
    const herdeiros = values.herdeiros_testamentarios || [];
    handleFieldChange('herdeiros_testamentarios', herdeiros.filter((_: any, i: number) => i !== index));
  };

  const handleAdicionarLegado = () => {
    if (novoLegado.trim()) {
      const legados = values.legados || [];
      handleFieldChange('legados', [...legados, novoLegado.trim()]);
      setNovoLegado('');
    }
  };

  const handleRemoverLegado = (index: number) => {
    const legados = values.legados || [];
    handleFieldChange('legados', legados.filter((_: any, i: number) => i !== index));
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Testamento Particular"
        customDescription="Disposição de última vontade escrita e assinada pelo testador"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dados do Testador */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados do Testador <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Nome completo, nacionalidade, estado civil, profissão, RG, CPF, endereço completo..."
            value={values.dados_testador || ''}
            onChange={(e) => handleFieldChange('dados_testador', e.target.value)}
          />
        </Box>

        {/* Herdeiros Necessários */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Herdeiros Necessários (descendentes, ascendentes, cônjuge)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Liste os herdeiros necessários (têm direito à legítima - 50% do patrimônio)..."
            value={values.herdeiros_necessarios || ''}
            onChange={(e) => handleFieldChange('herdeiros_necessarios', e.target.value)}
          />
        </Box>

        <Divider />

        {/* Herdeiros Testamentários */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Herdeiros/Legatários Testamentários
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Pessoas que receberão a parte disponível (50% do patrimônio)
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Nome, parentesco/relação, CPF, o que receberá..."
              value={novoHerdeiro}
              onChange={(e) => setNovoHerdeiro(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdicionarHerdeiro()}
            />
            <Button variant="outlined" onClick={handleAdicionarHerdeiro} startIcon={<AddIcon />}>
              Adicionar
            </Button>
          </Box>

          {(values.herdeiros_testamentarios || []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {(values.herdeiros_testamentarios || []).map((herdeiro: string, index: number) => (
                <Chip
                  key={index}
                  label={herdeiro}
                  onDelete={() => handleRemoverHerdeiro(index)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Legados Específicos */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Legados Específicos
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Bens específicos destinados a pessoas determinadas
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Ex: Apartamento na Rua X para João da Silva (CPF: xxx)"
              value={novoLegado}
              onChange={(e) => setNovoLegado(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdicionarLegado()}
            />
            <Button variant="outlined" onClick={handleAdicionarLegado} startIcon={<AddIcon />}>
              Adicionar
            </Button>
          </Box>

          {(values.legados || []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {(values.legados || []).map((legado: string, index: number) => (
                <Chip
                  key={index}
                  label={legado}
                  onDelete={() => handleRemoverLegado(index)}
                />
              ))}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Disposições Especiais */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Disposições Especiais
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Cláusulas especiais: inalienabilidade, impenhorabilidade, incomunicabilidade, usufruto, fideicomisso, nomeação de testamenteiro, etc."
            value={values.disposicoes_especiais || ''}
            onChange={(e) => handleFieldChange('disposicoes_especiais', e.target.value)}
          />
        </Box>

        {/* Testamenteiro */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Testamenteiro (executor do testamento)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Nome completo, CPF, endereço da pessoa que executará o testamento..."
            value={values.testamenteiro || ''}
            onChange={(e) => handleFieldChange('testamenteiro', e.target.value)}
          />
        </Box>

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com suas disposições de última vontade."
        />

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Documentos do testador, documentos dos bens."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="O testamento particular deve ser lido e assinado pelo testador na presença de 3 testemunhas, que também o assinarão." />
    </Box>
  );
};

